import { useMemo, useState } from "react";
import { ChevronRight, Plus, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Agent } from "@/api/agents";
import type { AgentPresence } from "@/api/agent-presence";
import { AgentAvatar } from "@/components/agent/agent-avatar";
import {
  activeTaskCount,
  groupFor,
  needsYouReason,
  presenceFor,
} from "@/hooks/use-agent-presence";

/**
 * Grouped agent list for the tenant sidebar (design 4a).
 *
 * Agents that need the user come first, running agents second, everything
 * else collapses into one "N more agents" row. Every row shows name + avatar;
 * status appears only when it means something (a reason pill, a task count, a
 * presence dot). Groups are omitted entirely when empty.
 *
 * The selected agent and any pinned agents stay visible even when idle, so
 * collapsing never hides the thing you are looking at.
 */
export function SidebarAgentList({
  agents,
  presence,
  selectedAgentId,
  pinnedAgentIds,
  onSelect,
  onContextMenu,
  onAdd,
  query,
  onQueryChange,
}: {
  agents: Agent[];
  presence: Record<string, AgentPresence> | undefined;
  selectedAgentId: string | null;
  pinnedAgentIds: string[];
  onSelect: (agentId: string) => void;
  onContextMenu?: (agentId: string, x: number, y: number) => void;
  onAdd?: () => void;
  query: string;
  onQueryChange: (q: string) => void;
}) {
  const [expandedIdle, setExpandedIdle] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const q = query.trim().toLowerCase();
  const filtered = q ? agents.filter((a) => a.name.toLowerCase().includes(q)) : agents;

  const groups = useMemo(() => {
    const needsYou: Agent[] = [];
    const running: Agent[] = [];
    const idle: Agent[] = [];
    for (const a of filtered) {
      const p = presenceFor(presence, a.id);
      const g = groupFor(p);
      (g === "needs-you" ? needsYou : g === "running" ? running : idle).push(a);
    }
    const activity = (a: Agent) => {
      const p = presenceFor(presence, a.id);
      return p.lastActivityAt ? new Date(p.lastActivityAt).getTime() : 0;
    };
    const byActivity = (x: Agent, y: Agent) => activity(y) - activity(x) || x.name.localeCompare(y.name);
    needsYou.sort(byActivity);
    running.sort(byActivity);
    // Idle: pinned first, then selected, then alphabetical — a stable order
    // for a list people scan by name.
    idle.sort((x, y) => {
      const px = pinnedAgentIds.includes(x.id) ? 0 : 1;
      const py = pinnedAgentIds.includes(y.id) ? 0 : 1;
      if (px !== py) return px - py;
      return x.name.localeCompare(y.name);
    });
    return { needsYou, running, idle };
  }, [filtered, presence, pinnedAgentIds]);

  // Always-visible idle rows: the selected agent and pinned agents. The rest
  // collapse behind the "N more agents" row unless expanded or searching.
  const alwaysVisible = groups.idle.filter(
    (a) => a.id === selectedAgentId || pinnedAgentIds.includes(a.id),
  );
  const collapsible = groups.idle.filter((a) => !alwaysVisible.includes(a));
  const showAllIdle = expandedIdle || q.length > 0;
  const visibleIdle = showAllIdle ? groups.idle : alwaysVisible;
  const hiddenCount = showAllIdle ? 0 : collapsible.length;

  const rowProps = (a: Agent) => ({
    key: a.id,
    agent: a,
    presence: presenceFor(presence, a.id),
    selected: a.id === selectedAgentId,
    onSelect: () => onSelect(a.id),
    onContextMenu: onContextMenu
      ? (e: React.MouseEvent) => {
          e.preventDefault();
          onContextMenu(a.id, e.clientX, e.clientY);
        }
      : undefined,
  });

  return (
    <div className="flex flex-col">
      {/* Section header: "AGENTS 12" with search + add. Same padding, icon
          boxes and title style as the Channels and Recents headers. */}
      <div className="mb-0.5 flex items-center gap-1.5 px-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.05em] text-text-muted">Agents</span>
        <span className="text-[10.5px] tabular-nums text-text-muted/70">{agents.length}</span>
        <span className="flex-1" />
        {agents.length > 6 && (
          <button
            type="button"
            onClick={() => {
              setSearchOpen((o) => {
                if (o) onQueryChange("");
                return !o;
              });
            }}
            aria-label={searchOpen ? "Close agent search" : "Search agents"}
            className={cn(
              "flex h-5 w-5 items-center justify-center rounded text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary",
              searchOpen && "text-text-primary",
            )}
          >
            {searchOpen ? <X size={12} /> : <Search size={12} />}
          </button>
        )}
        {onAdd && (
          <button
            type="button"
            onClick={onAdd}
            aria-label="New agent"
            title="New agent"
            className="flex h-5 w-5 items-center justify-center rounded text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary"
          >
            <Plus size={12} />
          </button>
        )}
      </div>

      {searchOpen && (
        <div className="mb-1 px-2">
          <input
            autoFocus
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                onQueryChange("");
                setSearchOpen(false);
              }
              if (e.key === "Enter" && filtered.length === 1) {
                e.preventDefault();
                onSelect(filtered[0]!.id);
              }
            }}
            placeholder="Filter agents…"
            aria-label="Filter agents"
            className="w-full rounded-md border border-border bg-background px-2 py-1 text-[12px] text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
          />
        </div>
      )}

      {agents.length === 0 && (
        <p className="px-2 py-2 text-[12px] text-text-muted">No agents yet</p>
      )}
      {agents.length > 0 && filtered.length === 0 && (
        <p className="px-2 py-2 text-[12px] text-text-muted">No agents match “{query.trim()}”</p>
      )}

      {groups.needsYou.length > 0 && (
        <GroupLabel tone="amber" label="Needs you" count={groups.needsYou.length} />
      )}
      {groups.needsYou.map((a) => <AgentRow {...rowProps(a)} group="needs-you" />)}

      {groups.running.length > 0 && (
        <GroupLabel tone="green" label="Running" count={groups.running.length} />
      )}
      {groups.running.map((a) => <AgentRow {...rowProps(a)} group="running" />)}

      {(groups.needsYou.length > 0 || groups.running.length > 0) &&
        (visibleIdle.length > 0 || hiddenCount > 0) && <div className="h-1.5" />}
      {visibleIdle.map((a) => <AgentRow {...rowProps(a)} group="idle" />)}

      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpandedIdle(true)}
          className="group flex w-full items-center gap-[9px] rounded-md px-[10px] py-[5px] text-left text-[12px] text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
        >
          <span className="inline-flex flex-none items-center">
            {collapsible.slice(0, 3).map((a, i) => (
              <span
                key={a.id}
                className={cn("inline-flex rounded-[5px] border-[1.5px] border-sidebar", i > 0 && "-ml-1.5")}
              >
                <AgentAvatar agent={a} size={16} neutral />
              </span>
            ))}
          </span>
          <span className="min-w-0 flex-1 truncate">
            {hiddenCount} more {hiddenCount === 1 ? "agent" : "agents"}
          </span>
          {/* 16px slot, the same as the + on channel rows, so it sits in that column. */}
          <span className="flex h-4 w-4 flex-none items-center justify-center">
            <ChevronRight size={12} className="text-text-muted" />
          </span>
        </button>
      )}
      {expandedIdle && !q && collapsible.length > 0 && (
        <button
          type="button"
          onClick={() => setExpandedIdle(false)}
          className="flex w-full items-center gap-[9px] rounded-md px-[10px] py-[5px] text-left text-[12px] text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
        >
          <span className="flex-1">Show less</span>
          <span className="flex h-4 w-4 flex-none items-center justify-center">
            <ChevronRight size={12} className="-rotate-90 text-text-muted" />
          </span>
        </button>
      )}
    </div>
  );
}

function GroupLabel({ tone, label, count }: { tone: "amber" | "green"; label: string; count: number }) {
  return (
    <p
      className={cn(
        "mb-0.5 mt-1.5 px-2 text-[10px] font-semibold uppercase tracking-[0.05em]",
        tone === "amber" ? "text-[#d97706] dark:text-[#f59e0b]" : "text-[#10a37f]",
      )}
    >
      {label} · {count}
    </p>
  );
}

function AgentRow({
  agent,
  presence,
  selected,
  group,
  onSelect,
  onContextMenu,
}: {
  agent: Agent;
  presence: AgentPresence;
  selected: boolean;
  group: "needs-you" | "running" | "idle";
  onSelect: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
}) {
  const reason = group === "needs-you" ? needsYouReason(presence) : null;
  const tasks = group === "running" ? activeTaskCount(presence) : 0;
  return (
    <button
      type="button"
      onClick={onSelect}
      onContextMenu={onContextMenu}
      aria-current={selected ? "true" : undefined}
      className={cn(
        "group flex w-full items-center gap-[9px] rounded-md px-[10px] py-[5px] text-left text-[12.5px] font-medium transition-colors",
        "hover:bg-surface-hover hover:text-text-primary",
        selected ? "bg-surface-hover text-text-primary" : group === "needs-you" ? "text-text-primary" : "text-text-secondary",
      )}
    >
      <AgentAvatar
        agent={agent}
        size={20}
        presence={group === "needs-you" ? "needs-you" : group === "running" ? "running" : null}
        ringClassName={selected ? "border-surface-hover" : "border-sidebar"}
      />
      <span className="min-w-0 flex-1 truncate">{agent.name}</span>
      {reason && (
        <span className="flex-none rounded-[4px] bg-[#d97706]/[0.13] px-[5px] py-px text-[10px] font-semibold text-[#d97706] dark:bg-[#f59e0b]/[0.13] dark:text-[#f59e0b]">
          {reason}
        </span>
      )}
      {tasks > 0 && (
        <span className="flex-none text-[10.5px] tabular-nums text-text-muted">
          {tasks} {tasks === 1 ? "task" : "tasks"}
        </span>
      )}
    </button>
  );
}
